import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import Footer from './components/Footer';
import Header from './components/Header';
import Loading from './components/Loading';

const HomePage = lazy(() => import('./HomePage'));
const About = lazy(() => import('./pages/about'));
const Contact = lazy(() => import('./pages/contact'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const PaymentPolicy = lazy(() => import('./pages/PaymentPolicy'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const Watch = lazy(() => import('./pages/watch'));
const Krishna = lazy(() => import('./pages/profile/krishna'));
const Keshav = lazy(() => import('./pages/profile/keshav'));
const Meena = lazy(() => import('./pages/profile/meena'));
const Naresh = lazy(() => import('./pages/profile/naresh'));
const Toursntravel = lazy(() => import('./pages/Toursntravel'));
const ComingSoon = lazy(() => import('./pages/ComingSoon'));
const Finance = lazy(() => import('./pages/finance'));
const OtherServices = lazy(() => import('./pages/OtherServices'));
const CreditCards = lazy(() => import('./pages/finance/CreditCards'));
const BankCardListPageLayout = lazy(() => import('./pages/finance/BankCardListPageLayout'));
const CreditCardPageLayout = lazy(() => import('./pages/finance/CreditCardPageLayout'));
const BankAccount = lazy(() => import('./pages/finance/BankAccount'));
const FiMoney = lazy(() => import('./pages/finance/FiMoney'));
const InsurancePolicy = lazy(() => import('./pages/finance/InsurancePolicy'));
const HDFCLife = lazy(() => import('./pages/finance/HDFCLife/HDFCLife'));
const HRServices = lazy(() => import('./pages/HRServices'));
const ITRFilling = lazy(() => import('./pages/ITRFilling'));
const GSTFilling = lazy(() => import('./pages/GSTFilling'));
const Jobs = lazy(() => import('./pages/jobs'));
const Doorbell = lazy(() => import('./pages/Doorbell'));
const Detective = lazy(() => import('./pages/detective'));
const BusinessConsultancy = lazy(() => import('./pages/BusinessConsultancy'));
const Charity = lazy(() => import('./pages/Charity'));
const DeviceCare = lazy(() => import('./pages/DeviceCare'));

const defaultHeaderBackground = 'linear-gradient(180deg, #000000 0%, #020617 100%)';

const headerThemes = [
  {
    match: (pathname) => pathname.startsWith('/about'),
    background: 'linear-gradient(135deg, #020405 0%, #07151a 54%, #0b1710 100%)',
  },
  {
    match: (pathname) => pathname.startsWith('/hr-services'),
    background: 'linear-gradient(180deg, #061015 0%, #0b1720 100%)',
  },
  {
    match: (pathname) => pathname.startsWith('/itr-filling'),
    background: 'linear-gradient(180deg, #071410 0%, #071b1b 100%)',
  },
  {
    match: (pathname) => pathname.startsWith('/gst-filling'),
    background: 'linear-gradient(180deg, #050b12 0%, #07131d 100%)',
  },
  {
    match: (pathname) => pathname.startsWith('/business-consultancy'),
    background: 'linear-gradient(135deg, #151006 0%, #10231f 54%, #06212a 100%)',
  },
  {
    match: (pathname) => pathname.startsWith('/detective'),
    background: 'linear-gradient(180deg, #060b11 0%, #08111a 100%)',
  },
  {
    match: (pathname) => pathname.startsWith('/finance/ip/hdfc-life'),
    background: 'linear-gradient(180deg, #020617 0%, #0f172a 100%)',
  },
  {
    match: (pathname) => pathname.startsWith('/finance') || pathname.startsWith('/other-services'),
    background: 'linear-gradient(180deg, #000000 0%, #020617 100%)',
  },
  {
    match: (pathname) => pathname.startsWith('/tours-and-travel'),
    background: 'linear-gradient(180deg, #000000 0%, #140b05 100%)',
  },
  {
    match: (pathname) => pathname.startsWith('/doorbell'),
    background: 'linear-gradient(180deg, #020617 0%, #0f172a 100%)',
  },
  {
    match: (pathname) => pathname.startsWith('/charity'),
    background: 'linear-gradient(180deg, #020403 0%, #03110a 100%)',
  },
];

function getHeaderBackground(pathname) {
  return headerThemes.find(({ match }) => match(pathname))?.background || defaultHeaderBackground;
}

function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);

  return null;
}

function AppRoutes() {
  const location = useLocation();
  const showHeader = location.pathname !== '/';
  const showFooter = location.pathname !== '/';
  const headerBackground = getHeaderBackground(location.pathname);

  return (
    <>
      {showHeader && (
        <div
          className="site-header-frame relative z-[100] mx-auto w-full px-3 pt-6 sm:px-4"
          style={{
            '--site-header-bg': headerBackground,
          }}
        >
          <Header />
        </div>
      )}

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-conditions" element={<TermsConditions />} />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/payment-policy" element={<PaymentPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/watch" element={<Watch />} />
        <Route path="/krishna" element={<Krishna />} />
        <Route path="/keshav" element={<Keshav />} />
        <Route path="/meena" element={<Meena />} />
        <Route path="/naresh" element={<Naresh />} />
        <Route path="/tours-and-travel" element={<Toursntravel />} />
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route path="/finance" element={<Finance />} />
        <Route path="/other-services" element={<OtherServices />} />
        <Route path="/finance/cc" element={<CreditCards />} />
        <Route path="/finance/cc/:bankSlug" element={<BankCardListPageLayout />} />
        <Route path="/finance/cc/:bankSlug/:cardSlug" element={<CreditCardPageLayout />} />
        <Route path="/finance/ac" element={<BankAccount />} />
        <Route path="/finance/ac/fi" element={<FiMoney />} />
        <Route path="/finance/ip" element={<InsurancePolicy />} />
        <Route path="/finance/ip/hdfc-life" element={<HDFCLife />} />
        <Route path="/hr-services" element={<HRServices />} />
        <Route path="/itr-filling" element={<ITRFilling />} />
        <Route path="/gst-filling" element={<GSTFilling />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/doorbell" element={<Doorbell />} />
        <Route path="/detective" element={<Detective />} />
        <Route path="/detective-services" element={<Detective />} />
        <Route path="/business-consultancy" element={<BusinessConsultancy />} />
        <Route path="/charity" element={<Charity />} />
        <Route path="/device-care" element={<DeviceCare />} />
      </Routes>

      {showFooter && <Footer />}
    </>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<Loading />}>
        <AppRoutes />
      </Suspense>
    </Router>
  );
}

export default App;
